import React, { useCallback, useEffect, useRef, useState } from 'react'
import { About, AppComingSoon, Home, Navbar, Resources, Scenes, UseCase } from './components'
import { Footer } from './containers'
import { APP_PAGE_ANCHOR } from './constants/navigation'

const pageAnchors = ['home', 'about', 'scenes', 'use-case', 'resources', APP_PAGE_ANCHOR]

const pageTitles = {
  home: 'OctoSynapse · Réactivez votre langue',
  about: 'À propos · OctoSynapse',
  scenes: 'Scènes immersives · OctoSynapse',
  'use-case': "Cas d'usage · OctoSynapse",
  resources: 'Conseils · OctoSynapse',
  [APP_PAGE_ANCHOR]: 'Expérience à venir · OctoSynapse',
}

const deepLinkTargets = {
  'use-case-cta': 'use-case-cta',
  'chooser-title': 'chooser-title',
}

const getLocationHash = () => {
  if (typeof window === 'undefined') return 'home'
  return window.location.hash.replace('#', '') || 'home'
}

const getCurrentAnchor = () => {
  if (typeof window === 'undefined') {
    return 'home'
  }

  const hash = getLocationHash()
  if (hash === 'use-case-cta') return 'use-case'
  if (hash === 'resources-form') return 'resources'
  if (hash === 'chooser-title') return 'scenes'
  return pageAnchors.includes(hash) ? hash : 'home'
}

const App = () => {
  const [navigation, setNavigation] = useState(() => ({
    activeAnchor: getCurrentAnchor(),
    locationHash: getLocationHash(),
    sequence: 0,
  }))
  const pageStageRef = useRef(null)
  const shouldFocusPage = useRef(false)
  const { activeAnchor, locationHash, sequence } = navigation

  useEffect(() => {
    const handleLocationChange = () => {
      shouldFocusPage.current = true
      setNavigation((current) => ({
        activeAnchor: getCurrentAnchor(),
        locationHash: getLocationHash(),
        sequence: current.sequence + 1,
      }))
    }

    window.addEventListener('hashchange', handleLocationChange)
    window.addEventListener('popstate', handleLocationChange)

    return () => {
      window.removeEventListener('hashchange', handleLocationChange)
      window.removeEventListener('popstate', handleLocationChange)
    }
  }, [])

  useEffect(() => {
    document.title = pageTitles[activeAnchor] || 'OctoSynapse'

    const deepLinkTarget = deepLinkTargets[locationHash]
    const target = deepLinkTarget ? document.getElementById(deepLinkTarget) : null

    if (target) {
      target.scrollIntoView?.({ block: 'start', behavior: 'instant' })
    } else if (sequence > 0) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
    }

    if (shouldFocusPage.current) {
      pageStageRef.current?.querySelector('main')?.focus({ preventScroll: true })
      shouldFocusPage.current = false
    }
  }, [activeAnchor, locationHash, sequence])

  const navigateToPage = useCallback((anchor) => {
    const nextHash = `#${anchor}`

    // Top-level navigation swaps the rendered page. Updating the URL through
    // history avoids the browser first scrolling to a same-named section on
    // the current page (the home page also contains preview section anchors).
    if (window.location.hash !== nextHash) {
      window.history.pushState(null, '', nextHash)
    }

    shouldFocusPage.current = true
    setNavigation((current) => ({
      activeAnchor: anchor,
      locationHash: anchor,
      sequence: current.sequence + 1,
    }))
  }, [])

  const skipToContent = useCallback((event) => {
    event.preventDefault()
    const mainContent = pageStageRef.current?.querySelector('main')
    mainContent?.scrollIntoView?.({ block: 'start', behavior: 'instant' })
    mainContent?.focus({ preventScroll: true })
  }, [])

  const renderPage = () => {
    if (activeAnchor === 'about') return <About />
    if (activeAnchor === 'scenes') return <Scenes />
    if (activeAnchor === 'use-case') return <UseCase />
    if (activeAnchor === 'resources') return <Resources />
    if (activeAnchor === APP_PAGE_ANCHOR) return <AppComingSoon />
    return <Home />
  }

  return (
    <div className={`app-shell${activeAnchor === 'home' ? ' app-shell--home' : ''}`}>
      <a className="skip-link" href={`#${activeAnchor}`} onClick={skipToContent}>
        <span lang="fr">Aller au contenu</span>
        <small lang="en">Skip to content</small>
      </a>
      <Navbar activePage={activeAnchor} onNavigate={navigateToPage} />
      <div className="page-stage" ref={pageStageRef}>
        {renderPage()}
      </div>
      <Footer />
    </div>
  )
}

export default App
